import Geohash from "latlon-geohash";
import { fetchedMappingDeviceTypes, fetchedMapPointTypes, MapDataCSVTypes } from "depot/map-page.depot";
import { formatDateOrDash } from "utils/util-functions";
import type { MappingHistoryExportPage } from "api/MappingHelpers";

export const MAP_HISTORY_CSV_HEADERS = [
  "Type",
  "Serial_Number",
  "IMEI",
  "Phone_Number",
  "Device_Alias",
  "DateTime_GPS",
  "RSSI",
  "RSRP",
  "LAT",
  "LONG",
  "Status",
];

interface FetchAllHistoryOptions {
  fetchPage: (cursor?: string) => Promise<MappingHistoryExportPage>;
  onProgress?: (exportedPoints: number) => void;
  expectedPointCount?: number;
}

export interface CompleteMappingHistory {
  device: fetchedMappingDeviceTypes;
  dataPoints: fetchedMapPointTypes[];
}

export class IncompleteMappingHistoryError extends Error {
  constructor() {
    super("Map history changed during export. Please retry the download.");
    this.name = "IncompleteMappingHistoryError";
    Object.setPrototypeOf(this, IncompleteMappingHistoryError.prototype);
  }
}

export const fetchMappingHistoryWithRetry = async <T>(fetchAttempt: () => Promise<T>): Promise<T> => {
  try {
    return await fetchAttempt();
  } catch (error) {
    if (!(error instanceof IncompleteMappingHistoryError)) {
      throw error;
    }
    return fetchAttempt();
  }
};

export const fetchAllMappingHistory = async ({
  fetchPage,
  onProgress,
  expectedPointCount,
}: FetchAllHistoryOptions): Promise<CompleteMappingHistory> => {
  const dataPoints: fetchedMapPointTypes[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | undefined;
  let device: fetchedMappingDeviceTypes | undefined;

  do {
    // Export pages are cursor-dependent and must be requested sequentially.
    // eslint-disable-next-line no-await-in-loop
    const page = await fetchPage(cursor);
    device = page.device;
    dataPoints.push(...page.dataPoints);
    onProgress?.(dataPoints.length);

    if (page.nextCursor && seenCursors.has(page.nextCursor)) {
      throw new Error("Mapping history export returned a repeated cursor.");
    }

    cursor = page.nextCursor;
    if (cursor) {
      seenCursors.add(cursor);
    }
  } while (cursor);

  if (!device) {
    throw new Error("Mapping history export returned no pages.");
  }

  if (expectedPointCount !== undefined && dataPoints.length !== expectedPointCount) {
    throw new IncompleteMappingHistoryError();
  }

  return { device, dataPoints };
};

const decodeGeohash = (geohash?: string): { lat?: number; lon?: number } => {
  if (!geohash) {
    return {};
  }

  try {
    return Geohash.decode(geohash);
  } catch {
    return {};
  }
};

export const getMapHistoryCsvRows = (
  device: fetchedMappingDeviceTypes,
  dataPoints: fetchedMapPointTypes[],
): MapDataCSVTypes[] =>
  [...dataPoints]
    .sort((first, second) => first.timeStamp - second.timeStamp)
    .map(({ geohash, rssi, rsrp, timeStamp }) => {
      const { lat, lon } = decodeGeohash(geohash);
      return {
        type: "Map data points",
        sn: `${device.sn}\t`,
        imei: `${device.imei}\t`,
        ph: `${device.ph}\t`,
        al: device.al,
        DateTime_GPS: formatDateOrDash(timeStamp, "YYYY/MM/DD HH:mm:ss"),
        rssi: rssi === undefined ? "" : `${rssi}`,
        rsrp: rsrp === undefined ? "" : `${rsrp}`,
        lat,
        lon,
        status: rsrp === 0 && rssi === 0 ? "Offline" : "Online",
      };
    });

const escapeCsvValue = (value: unknown): string => {
  const text = value === undefined || value === null ? "" : `${value}`;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const createMapHistoryCsvBlob = (
  device: fetchedMappingDeviceTypes,
  dataPoints: fetchedMapPointTypes[],
): Blob => {
  const rows = getMapHistoryCsvRows(device, dataPoints);
  const csvLines = [
    MAP_HISTORY_CSV_HEADERS.map(escapeCsvValue).join(","),
    ...rows.map((row) => Object.values(row).map(escapeCsvValue).join(",")),
  ];

  return new Blob([`\uFEFF${csvLines.join("\r\n")}\r\n`], { type: "text/csv;charset=utf-8" });
};

export const downloadBlob = (blob: Blob, fileName: string): void => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
};
